import _ from 'underscore';
import dayjs from '../../../../shared/services/dayjs.js';
import { Egfz } from '../../../../shared/services/Egfz.js';
import { Roles } from '../../../../../domain/konstanten/Roles.js';
import { secureHTML, findePosten } from '../util.js';
import { istSchichtTreffer } from './filterPanel.js';

// Relevante HTML Elemente auf die oft zugegriffen wird
const schichtDivTemplate = document.querySelector('[data-schicht-template]');
const schichtPanelTemplate = document.querySelector('[panel-schicht-template]');

// Uhrzeit (HH:mm) in der Standard-Zeitzone. Ein wiederverwendeter Intl.DateTimeFormat statt dayjs().tz(),
// das bei jedem Aufruf intern einen neuen Formatter erzeugt (teuer bei vielen Schichten). Lazy angelegt,
// damit dayjs.tz.DEFAULT_TZ sicher gesetzt ist; hourCycle h23 verhindert "24:00" um Mitternacht.
let zeitFormat;
function uhrzeit(datum) {
	zeitFormat ??= new Intl.DateTimeFormat('de-DE', {
		hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: dayjs.tz.DEFAULT_TZ,
	});
	return zeitFormat.format(new Date(datum));
}

/**
 * Erstellt ein kleines Schicht Panel für das Anzeigen von geplanten Schichten außerhalb des Postens.
 * @param {object} schicht
 * @param {string} gridId
 * @param {array<object>} postenListe
 */
function createSchichtPanel(schicht, gridId, postenListe) {
	// Die Grid Zelle, in der wir das Schichtpanel anzeigen wollen.
	const parent = document.getElementById(gridId);
	// wenn der Mitarbeiter nicht im Posten ist, überspringen wir die Schicht
	if (parent === null) {
		return;
	}
	const schichtPanel = schichtPanelTemplate.content.cloneNode(true).children[0];
	const schichtTitel = schichtPanel.querySelector('[schicht-titel]');
	const schichtInfo = schichtPanel.querySelector('[schicht-beschreibung]');
	const titel = secureHTML(schicht.Titel);
	let schichtBeschreibung = `${secureHTML(uhrzeit(schicht.SchichtVon))} - ${secureHTML(uhrzeit(schicht.SchichtBis))}`;
	if (!schicht.OffenesEnde && schicht.OffenerBeginn) {
		schichtBeschreibung = `bis ${secureHTML(uhrzeit(schicht.SchichtBis))}`;
	}
	if (schicht.OffenesEnde && !schicht.OffenerBeginn) {
		schichtBeschreibung = `ab ${secureHTML(uhrzeit(schicht.SchichtVon))}`;
	}
	if (schicht.OffenesEnde && schicht.OffenerBeginn) {
		schichtBeschreibung = 'offen';
	}
	schichtTitel.innerText = titel;
	schichtPanel.setAttribute('aria-label', schicht._id);
	const posten = findePosten(postenListe, schicht.PostenID);
	schichtPanel.title = `${schicht.Bereich ? `${schicht.Bereich}, ` : ''}${posten?.Posten || ''}: ${schichtBeschreibung}`;
	schichtInfo.innerText = `${schicht.Bereich ? `${schicht.Bereich}: ${schicht.ZeitTyp === 2 ? schichtBeschreibung : ''}` : `${schicht.ZeitTyp === 2 ? schichtBeschreibung : ''}` }`;
	// gridId of the shift's own Posten cell — may not be in DOM if that Bereich is disabled
	const schichtGridId = `${schicht.Wochentag.Zahl}:${schicht.PostenID}:${schicht.MitarbeiterID}`;
	const removeButton = schichtPanel.querySelector('[span-remove]');
	// Wenn der User Zugriff auf den Bereich der Schicht hat, aber die Schicht nicht in seinem Posten ist, soll er die Schicht entfernen können, da sie ja in seinem Bereich liegt. Ansonsten soll er die Schicht nicht entfernen können, da sie außerhalb seines Bereichs liegt.
	const isInUserBereich = window.myVars.UserBereiche.length === 0 || window.myVars.UserBereiche.includes(schicht.BereichID);
	if (!schicht.PostenID || _.isEmpty(posten) || isInUserBereich) {
		removeButton.setAttribute('onclick', `fun.schichtEntfernen("${schicht._id}", event, "${schichtGridId}")`);
	} else {
		removeButton.style.display = 'none';
	}
	parent.insertAdjacentElement('afterbegin', schichtPanel);

	// Unter Krank Panels keine Schicht Adden
	if ([Egfz.Krank, Egfz.KindKrank, Egfz.UnentschuldigtKrank].includes(schicht.Zeitbloecke[0].EgfzID)) {
		schichtPanel.classList.add('panel-krank');
		if (parent.querySelector('.span-add')) {
			parent.querySelector('.span-add').style.display = 'none';
		}
	}

	// entferne den Daumen hoch/runter falls eine Schicht eingefügt wurde
	parent.classList.add('wunschzeit-hide');
}

/**
 * Erstellt ein HTML Element aus dem Schicht Template
 * @param {object} schicht das SchichtOjekt
 * @param {*} gridId ID des HTML Grid Elements
 */
function createSchichtElement(schicht, gridId) {
	// Die Grid Zelle, in der wir die neue Schicht anzeigen wollen.
	const parent = document.getElementById(gridId);
	/**
	 * Falls wir kein Mitarbeiter Feld in diesem Posten finden aus irgendeinen Grund,
	 * z.B. hat der Mitarbeiter eine Schicht in diesem Posten, welche ihm nicht angehört,
	 * dann überspringen wir diesen...
	 */
	if (parent === null) {
		console.info('Diese Schicht hat keine Zuordnung, PostenID oder MitarbeiterID nicht mehr aktuell:', schicht._id);
		return;
	}
	const schichtElement = schichtDivTemplate.content.cloneNode(true).children[0];
	// --schicht-farbe statt direktem border-color: dienstplan.css leitet daraus per color-mix()
	// zusätzlich einen hellen Hintergrundton ab (Rand in voller Farbe, Fläche abgetönt).
	if (schicht.SchichtFarbe) {
		schichtElement.style.setProperty('--schicht-farbe', schicht.SchichtFarbe);
	}
	const schichtTitel = schichtElement.querySelector('[schicht-titel]');
	const schichtBeschreibung = schichtElement.querySelector('[schicht-beschreibung]');
	const titel = secureHTML(schicht.Titel);
	const beschreibung = `${secureHTML(uhrzeit(schicht.SchichtVon))} - ${secureHTML(uhrzeit(schicht.SchichtBis))}`;
	schichtTitel.innerText = titel;
	schichtBeschreibung.innerText = beschreibung;
	// Einsatzort-Pill nur anzeigen, wenn der Schicht einer zugeordnet ist (schicht.Einsatzort ist der
	// bereits aufgelöste Name, siehe SchichtObjektKeys.js — analog zu schicht.Posten/schicht.Bereich).
	// Bei EGFZ-Schichten (Krank, Urlaub, ...) ist der Einsatzort nicht relevant. Maßgeblich ist die
	// EinsatzortID — der Name ohne Zuordnung ist nur der Platzhalter '-' (LeereSchicht.js, SchichtService).
	const schichtEinsatzort = schichtElement.querySelector('[schicht-einsatzort]');
	if (schicht.EinsatzortID && schicht.Einsatzort && schicht.ZeitTyp === 2) {
		schichtEinsatzort.innerHTML = `<i class="bi bi-geo-alt-fill"></i>${secureHTML(schicht.Einsatzort)}`;
	} else {
		schichtEinsatzort.style.display = 'none';
	}
	const schichtID = schicht._id;
	schichtElement.ariaLabel = schichtID;
	// Falls die Schicht aktuell als Kopierquelle markiert ist, Markierung wieder anbringen
	// (z.B. nach Re-Render durch Wochen-/Bereich-/Betriebswechsel zurück zur Quellwoche).
	if (window.fun?.istKopierQuelleSchicht?.(String(schichtID))) {
		schichtElement.classList.add('schicht-kopier-quelle');
	}
	// Titel passt zur Suche im Filter-Panel → Karte hervorheben (zeigt, warum die Zeile ein Treffer ist).
	if (istSchichtTreffer(schicht)) {
		schichtElement.classList.add('such-treffer');
	}
	// Falls es sich um eine EGFZ Schicht handelt, werden wir kein Editieren erlauben, Löschen schon falls krank/schule Schicht
	if (schicht.ZeitTyp !== 2) {
		schichtElement.classList.add('egfz-schicht');
		// Krank/Urlaub bekommen eine eigene, vom Reference-Design abweichende Einfärbung (rot/akzent
		// statt dem generischen gestrichelten egfz-Look), alle übrigen EGFZ-Typen bleiben generisch.
		if ([Egfz.Krank, Egfz.KindKrank, Egfz.UnentschuldigtKrank].includes(schicht.Zeitbloecke[0].EgfzID)) {
			schichtElement.classList.add('egfz-krank');
		} else if (schicht.Zeitbloecke[0].EgfzID === Egfz.Urlaub) {
			schichtElement.classList.add('egfz-urlaub');
			const urlaubIcon = document.createElement('i');
			urlaubIcon.className = 'bi bi-airplane-fill tag-urlaub';
			schichtElement.insertBefore(urlaubIcon, schichtElement.firstChild);
		}
		// Nur bei Gutzeiten und Arbeit am FT interessiert uns hier die Uhrzeit...
		if (![Egfz.GZAufbau, Egfz.GZAbbau, Egfz.ArbeitAmFeiertag].includes(schicht.Zeitbloecke[0].EgfzID)) {
			schichtBeschreibung.style.display = 'none';
		}
		// Bei EGFZ Schichten verhalten sich die span tools etwas anders (nur Notiz bearbeiten und löschen)
		let egfzOptions = [Egfz.Krank, Egfz.KindKrank, Egfz.UnentschuldigtKrank, Egfz.Schule, Egfz.GZAbbau, Egfz.UnbezahltFrei, Egfz.Feiertag];
		const spanDiv = schichtElement.querySelector('[span-div]');
		spanDiv.style.display = 'flex';
		spanDiv.querySelector('[span-edit]').setAttribute('onclick', `fun.schichtNotizBearbeiten("${schichtID}", event, "${gridId}")`);
		spanDiv.querySelector('[span-edit]').title = 'Schichtnotiz hinzufügen';
		// Dienstplaner darf kein Urlaub löschen
		if (!window.myVars.UserRoles.includes(Roles.Dienstplaner)) {
			egfzOptions.push(Egfz.Urlaub);
		} else if (schicht.Zeitbloecke[0].EgfzID === Egfz.Urlaub) {
			spanDiv.querySelector('[span-remove]').style.display = 'none';
		}
		if (egfzOptions.includes(schicht.Zeitbloecke[0].EgfzID)) {
			spanDiv.querySelector('[span-remove]').setAttribute('onclick', `fun.schichtEntfernen("${schichtID}", event, "${gridId}")`);
			// Bei Krank keine weiteren Schichten anbieten
			if (parent.querySelector('.span-add')) {
				parent.querySelector('.span-add').style.display = 'none';
			}
		}
		// Bei Urlaub wollen wir erst die Schicht, dann das Urlaubsicon
		parent.insertAdjacentElement('afterbegin', schichtElement);
	} else {
		// Freigabe icon anzeigen (fadet beim Hover aus, siehe CSS) + Kopier-Tool zwischen Bearbeiten und Löschen
		const schichtFreigabeStatus = schichtElement.querySelector('[schicht-freigabe-status]');
		schichtFreigabeStatus.innerHTML = schicht.SchichtFreigabe
			? '<i class="bi bi-circle-fill" title="Schicht freigegeben"></i>'
			: '<i class="bi bi-circle" title="Schicht noch nicht freigegeben"></i>';
		// Posten als aria Attribut speichern
		schichtElement.setAttribute('aria-description', schicht.PostenID);
		// Event Listener einbauen
		schichtElement.setAttribute('onclick', `fun.schichtEditieren("${schichtID}", event, "${gridId}")`);
		const spanDiv = schichtElement.querySelector('[span-div]');
		spanDiv.querySelector('[span-edit]').setAttribute('onclick', `fun.schichtEditieren("${schichtID}", event, "${gridId}")`);
		spanDiv.querySelector('[span-remove]').setAttribute('onclick', `fun.schichtEntfernen("${schichtID}", event, "${gridId}")`);
		// Kopier-Tool (Design: Stift · Kopieren · Papierkorb) zwischen Bearbeiten und Löschen einfügen
		const kopierTool = document.createElement('span');
		kopierTool.className = 'span-tools span-copy hidden-print';
		kopierTool.title = 'Schicht zum Kopieren markieren';
		kopierTool.innerHTML = '<i class="bi bi-copy"></i>';
		kopierTool.addEventListener('click', (event) => {
			event.stopPropagation();
			window.fun?.markiereKopierQuelle?.(schichtID, schichtElement);
		});
		spanDiv.querySelector('[span-remove]').insertAdjacentElement('beforebegin', kopierTool);
		if (!schicht.OffenesEnde && schicht.OffenerBeginn) {
			schichtBeschreibung.innerText = `bis ${secureHTML(uhrzeit(schicht.SchichtBis))}`;
		}
		if (schicht.OffenesEnde && !schicht.OffenerBeginn) {
			schichtBeschreibung.innerText = `ab ${secureHTML(uhrzeit(schicht.SchichtVon))}`;
		}
		if (schicht.OffenesEnde && schicht.OffenerBeginn) {
			schichtBeschreibung.innerText = '';
		}
		if (parent.querySelector('.span-add')) {
			parent.querySelector('.span-add').insertAdjacentElement('beforebegin', schichtElement);
		} else {
			parent.appendChild(schichtElement);
		}
	}

	// entferne den Daumen hoch/runter falls eine Schicht eingefügt wurde
	parent.classList.add('wunschzeit-hide');
}

export { createSchichtElement, createSchichtPanel };
